"use client";

import * as React from "react";
import { useState } from "react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import { Label } from "@baluu/ui/components/label";
import { Input } from "@baluu/ui/components/input";
import { Textarea } from "@baluu/ui/components/textarea";
import { RadioGroup, RadioGroupItem } from "@baluu/ui/components/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@baluu/ui/components/select";
import { CheckoutSteps } from "@baluu/ui/components/checkout/checkout-steps";
import { Check, Minus, Plus, Gift, Package, Truck } from "lucide-react";

// Types
type PostageOption = {
  id: string;
  name: string;
  description?: string;
  price: number;
  deliveryTimeFrom?: number;
  deliveryTimeTo?: number;
  deliveryUnit?: string;
};

type GiftingDetails = {
  to: string;
  from: string;
  message: string;
};

type ProductSelectionStepProps = {
  productName: string;
  productPrice: number;
  productStock: number;
  currency?: string;
  postageOptions: PostageOption[];
  selectedQuantity: number;
  selectedPostageId: string | null;
  isGift: boolean;
  giftingDetails?: GiftingDetails;
  giftingEnabled?: boolean;
  giftingFee?: number;
  doTeacherWraps?: boolean;
  onQuantityChange: (quantity: number) => void;
  onPostageChange: (postageId: string) => void;
  onGiftChange: (isGift: boolean) => void;
  onGiftingDetailsChange?: (details: GiftingDetails) => void;
  className?: string;
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  AUD: "A$",
  CAD: "C$",
  NZD: "NZ$",
};

function formatPrice(price: number, currency = "GBP"): string {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${(price / 100).toFixed(2)}`;
}

function ProductSelectionStep({
  productName,
  productPrice,
  productStock,
  currency = "GBP",
  postageOptions,
  selectedQuantity,
  selectedPostageId,
  isGift,
  giftingDetails = { to: "", from: "", message: "" },
  giftingEnabled = false,
  giftingFee = 0,
  doTeacherWraps = false,
  onQuantityChange,
  onPostageChange,
  onGiftChange,
  onGiftingDetailsChange,
  className,
}: ProductSelectionStepProps) {
  return (
    <div className={cn("space-y-6", className)}>
      <CheckoutSteps.Header
        title="Product Selection"
        description="Select quantity and delivery preference."
      />

      {/* Quantity Selection */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="font-medium text-foreground mb-4">
          How many products do you want?
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          All products will be delivered to the same address (we ask for this
          next)
        </p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center border rounded-lg">
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  (selectedQuantity ?? 1) > 1 &&
                  onQuantityChange((selectedQuantity ?? 1) - 1)
                }
                disabled={(selectedQuantity ?? 1) <= 1}
                className="h-10 w-10 rounded-r-none"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-12 text-center font-medium">
                {selectedQuantity ?? 1}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  (selectedQuantity ?? 1) < (productStock ?? 99) &&
                  onQuantityChange((selectedQuantity ?? 1) + 1)
                }
                disabled={(selectedQuantity ?? 1) >= (productStock ?? 99)}
                className="h-10 w-10 rounded-l-none"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <span className="text-sm text-muted-foreground">
              {productStock ?? 99} in stock
            </span>
          </div>
          <div className="text-right">
            <p className="font-bold text-lg">
              {formatPrice(
                (productPrice ?? 0) * (selectedQuantity ?? 1),
                currency,
              )}
            </p>
            {(selectedQuantity ?? 1) > 1 && (
              <p className="text-sm text-muted-foreground">
                {formatPrice(productPrice ?? 0, currency)} each
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Postage Selection */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="font-medium text-foreground mb-4">
          Select your delivery preference
        </h3>

        <div className="space-y-3">
          {postageOptions.map((postage) => (
            <div
              key={postage.id}
              onClick={() => onPostageChange(postage.id)}
              className={cn(
                "flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all",
                selectedPostageId === postage.id
                  ? "border-primary bg-primary/20"
                  : "border-border hover:border-muted-foreground/50",
              )}
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                    selectedPostageId === postage.id
                      ? "border-primary bg-primary"
                      : "border-border",
                  )}
                >
                  {selectedPostageId === postage.id && (
                    <Check className="h-3 w-3 text-primary-foreground" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-foreground">
                    {postage.name}
                  </p>
                  {postage.deliveryTimeFrom && postage.deliveryTimeTo && (
                    <p className="text-sm text-muted-foreground">
                      Delivery in {postage.deliveryTimeFrom}-
                      {postage.deliveryTimeTo}{" "}
                      {postage.deliveryUnit || "working days"}
                    </p>
                  )}
                  {postage.description && (
                    <p className="text-sm text-muted-foreground">
                      {postage.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-muted-foreground/70" />
                <span className="font-semibold">
                  {postage.price === 0
                    ? "Free"
                    : formatPrice(postage.price, currency)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gifting Option */}
      {giftingEnabled && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-start gap-3 mb-4">
            <Gift className="h-5 w-5 text-muted-foreground/70 mt-0.5" />
            <div>
              <h3 className="font-medium text-foreground">
                Would you like to give this as a gift?
              </h3>
              <p className="text-sm text-muted-foreground">
                {doTeacherWraps
                  ? "This will include a personalised message to your recipient and gift wrapping of the product itself."
                  : "This will include a personalised message to your recipient."}
                {giftingFee > 0 &&
                  ` (${formatPrice(giftingFee, currency)} gifting fee)`}
              </p>
            </div>
          </div>

          <RadioGroup
            value={isGift ? "yes" : "no"}
            onValueChange={(value) => onGiftChange(value === "yes")}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="gift-no" />
              <Label htmlFor="gift-no">No</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="gift-yes" />
              <Label htmlFor="gift-yes">Yes</Label>
            </div>
          </RadioGroup>

          {/* Gift Details Form */}
          {isGift && (
            <div className="mt-4 space-y-4 pt-4 border-t border-border/50">
              <p className="text-sm text-muted-foreground font-medium">
                Please leave a message for your gift recipient below
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gift-to">To *</Label>
                  <Input
                    id="gift-to"
                    placeholder="Recipient's name"
                    value={giftingDetails.to}
                    onChange={(e) =>
                      onGiftingDetailsChange?.({
                        ...giftingDetails,
                        to: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gift-from">From *</Label>
                  <Input
                    id="gift-from"
                    placeholder="Your name"
                    value={giftingDetails.from}
                    onChange={(e) =>
                      onGiftingDetailsChange?.({
                        ...giftingDetails,
                        from: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gift-message">Message *</Label>
                <Textarea
                  id="gift-message"
                  placeholder="Write a personal message for the recipient..."
                  value={giftingDetails.message}
                  onChange={(e) =>
                    onGiftingDetailsChange?.({
                      ...giftingDetails,
                      message: e.target.value,
                    })
                  }
                  rows={4}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { ProductSelectionStep };
export type { ProductSelectionStepProps, PostageOption, GiftingDetails };
